/**
 * Which filter "Clear last filter" drops next.
 *
 * Nothing records the order filters were actually applied in, so "last" is
 * approximated by a fixed priority: narrowest first, broadest last. Pulled out
 * of useSearchFilters so the ordering is testable on its own — it is easy to
 * reorder by accident and the consequence is only visible three clicks into the
 * storefront.
 */

/**
 * Order the multi-select filters are cleared in.
 *
 * `collections` is deliberately last. It used to sit ahead of `brands` and
 * `categories`, so on /collections/cartridges the button discarded the
 * collection the page is named after and kept the brand the shopper had just
 * chosen. Because removing the route's collection also demotes the page to
 * /search, a single click both dropped the wrong filter and moved the page out
 * from under the shopper. On a collection route the collection is the page's
 * identity rather than something opted into, so it goes last.
 */
export const CLEAR_LAST_FILTER_ORDER = [
  "carrierOils",
  "extractionProcesses",
  "regionsOfProduct",
  "provincesOfOrigin",
  "flavors",
  "dominantTerpenes",
  "strains",
  "types",
  "growingMethods",
  "sizes",
  "subCategories",
  "brands",
  "categories",
  "collections",
] as const;

export type ClearableListKey = (typeof CLEAR_LAST_FILTER_ORDER)[number];

/**
 * The next list filter to pop a value from, or null when every list is empty
 * (the caller then falls through to the scalar filters: ranges, strain type,
 * on-sale, free-text query).
 */
export function nextListFilterToClear(
  lists: Partial<Record<ClearableListKey, readonly string[] | undefined>>
): ClearableListKey | null {
  for (const key of CLEAR_LAST_FILTER_ORDER) {
    const values = lists[key];
    if (Array.isArray(values) && values.length > 0) return key;
  }
  return null;
}
